import { useMutation, useQuery } from '@apollo/client/react';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_USER_PROFILES_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { UPDATE_USER_PROFILE_ROLES_MUTATION } from '../../integrations/allkort/graphql/gql/UserProfileMutations';
import { BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { UpdateUserProfileRolesResponse } from '../../integrations/allkort/graphql/interface/UserProfile';

/**
 * useUpdateUserProfileRoles
 *
 * Custom hook for assigning or removing roles on a user profile.
 * Fetches the current customer's ID from cache automatically.
 *
 * @example
 * ```tsx
 * const { updateUserProfileRoles, loading, error } = useUpdateUserProfileRoles();
 *
 * await updateUserProfileRoles({
 *   userProfileId: 285507,
 *   rolesToAdd: ['ReadCards', 'ReadInvoices'],
 *   rolesToRemove: [],
 * });
 * ```
 */
export const useUpdateUserProfileRoles = () => {
	const { data: customerData } = useQuery<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		{ fetchPolicy: 'network-only' }
	);

	const [updateUserProfileRolesMutation, { loading, error, data }] =
		useMutation<UpdateUserProfileRolesResponse>(UPDATE_USER_PROFILE_ROLES_MUTATION, {
			refetchQueries: [{ query: GET_USER_PROFILES_QUERY }],
			awaitRefetchQueries: true,
		});

	const updateUserProfileRoles = async (input: {
		userProfileId: number
		rolesToAdd: string[]
		rolesToRemove: string[]
	}) => {
		const customerId = customerData?.currentCustomer?.id;
		if (!customerId) {
			throw new Error('Customer ID not available');
		}

		try {
			const result = await updateUserProfileRolesMutation({
				variables: {
					customerId,
					userProfileId: input.userProfileId,
					rolesToAdd: input.rolesToAdd,
					rolesToRemove: input.rolesToRemove,
				},
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useUpdateUserProfileRoles] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.updateUserProfileRoles?.userProfile ?? null;
		} catch (err) {
			throw err;
		}
	};

	return { updateUserProfileRoles, loading, error, data };
};
